import { useEffect, useRef } from 'react';
import {
  decideAdaptiveQuality,
  VIDEO_QUALITY_ORDER,
  type VideoQualityTier,
  type WebRTCNetworkMetrics,
} from '../utils/videoQuality.ts';

const CHECK_INTERVAL_MS = 5000;
// İki kalite değişimi arasında beklenmesi gereken süre. Sık kademe
// geçişleri karşı tarafta çözünürlük değişimi olarak "titreme" yaratır.
const QUALITY_CHANGE_COOLDOWN_MS = 30_000;

interface UseAdaptiveVideoQualityOptions {
  enabled: boolean;
  currentQuality: VideoQualityTier;
  collectNetworkMetrics: () => Promise<WebRTCNetworkMetrics | null>;
  onQualityChange: (quality: VideoQualityTier) => void;
}

export function useAdaptiveVideoQuality({
  enabled,
  currentQuality,
  collectNetworkMetrics,
  onQualityChange,
}: UseAdaptiveVideoQualityOptions) {
  const qualityRef = useRef(currentQuality);
  const badStreakRef = useRef(0);
  const goodStreakRef = useRef(0);
  const lastChangeAtRef = useRef(0);

  useEffect(() => {
    qualityRef.current = currentQuality;
  }, [currentQuality]);

  useEffect(() => {
    if (!enabled) {
      badStreakRef.current = 0;
      goodStreakRef.current = 0;
      return;
    }

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;

      try {
        const metrics = await collectNetworkMetrics();
        if (!metrics || metrics.peerCount === 0) return;

        const result = decideAdaptiveQuality(
          qualityRef.current,
          metrics,
          badStreakRef.current,
          goodStreakRef.current
        );

        badStreakRef.current = result.badStreak;
        goodStreakRef.current = result.goodStreak;

        if (result.next !== qualityRef.current) {
          const now = Date.now();
          const isDowngrade =
            VIDEO_QUALITY_ORDER.indexOf(result.next) <
            VIDEO_QUALITY_ORDER.indexOf(qualityRef.current);

          // Yükseltmeler için bekleme süresi uygula; düşürme (ağ kötü) hemen yapılır.
          if (!isDowngrade && now - lastChangeAtRef.current < QUALITY_CHANGE_COOLDOWN_MS) {
            return;
          }

          lastChangeAtRef.current = now;
          qualityRef.current = result.next;
          onQualityChange(result.next);
        }
      } catch (error) {
        console.warn('[AdaptiveQuality] ölçüm hatası:', error);
      }
    };

    const interval = window.setInterval(() => {
      void tick();
    }, CHECK_INTERVAL_MS);

    void tick();

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [enabled, collectNetworkMetrics, onQualityChange]);
}
